import { useState, useEffect, useRef } from "react";
import EntityContextMenu from "@/components/EntityContextMenu";
import { FolderColorStrip } from "@/components/FolderColorStrip";
import CharacterSessionHistory from "@/components/CharacterSessionHistory";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { ImageDropZone } from "@/components/ImageDropZone";
import { SlidePanel } from "@/components/SlidePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Edit, Trash2, Search, Upload, UserCircle2, MapPin, X, Shield, Heart, Footprints, Eye, Wand2, Lock, CheckSquare } from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";
import AICreateMode from "@/components/AICreateMode";
import { FolderRail, FolderToolbar, FolderCard, partitionCurrentLevel, useFolderCrud } from "@/components/FolderGrid";
import { useMultiSelect, BulkActionBar, SelectCheckbox } from "@/components/MultiSelect";
import { RelatedEntitiesPanel } from "@/components/RelatedEntitiesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Resolve an entity image field: pass through full URLs, otherwise serve the
// storage key through the authenticated /api/files proxy.
const resolveImg = (val) => (!val ? null : (val.startsWith("http") ? val : `${API}/files/${val}`));

const PROFESSIONS = [
  "", "Shopkeeper", "Blacksmith", "Innkeeper", "Merchant", "Soldier", "Guard",
  "General", "Noble", "Priest", "Scholar", "Farmer", "Hunter", "Bard",
  "Healer", "Alchemist", "Thief", "Assassin", "Spy", "Sailor",
  "Chef", "Stableboy", "Cleaner", "Maid", "Miller", "Apothecary",
  "Fisherman", "Gravedigger", "Scribe", "Tanner", "Brewer", "Carpenter", "Other"
];
const RACES = ["Human", "Elf", "Dwarf", "Halfling", "Dragonborn", "Gnome", "Half-Elf", "Half-Orc", "Tiefling", "Half-Goblin", "Genasi", "Tortle", "Other"];

const NPCsPage = () => {
  const { campaignId } = useParams();
  const { user } = useAuth();
  const isCommoner = (user?.tier || "commoner") === "commoner";
  const [campaign, setCampaign] = useState(null);
  const [npcs, setNPCs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createMode, setCreateMode] = useState("manual");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProfession, setFilterProfession] = useState("all");
  const [uploadingImageFor, setUploadingImageFor] = useState(null);
  const [selectedNPC, setSelectedNPC] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [folders, setFolders] = useState([]);
  const folderCrud = useFolderCrud({ entityType: "npcs", onRefresh: () => fetchData() });
  useEffect(() => { setFolders(folderCrud.folders); }, [folderCrud.folders]);
  const multi = useMultiSelect();
  const imageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "", description: "", race: "", profession: "",
    location_id: "", personality: "",
    armor_class: "", hit_points: "", speed: "", initiative: "",
    folder_id: ""
  });

  useEffect(() => { fetchData(); }, [campaignId]);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const fid = searchParams.get("focus");
    if (fid && npcs.length) { const n = npcs.find(x => x.id === fid); if (n) setSelectedNPC(n); }
  }, [searchParams, npcs]);

  const fetchData = async () => {
    try {
      const [campRes, npcsRes, locsRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/npcs`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true }),
      ]);
      setCampaign(campRes.data);
      setNPCs(npcsRes.data);
      setLocations(locsRes.data);
      folderCrud.refresh();
      // Keep selected NPC in sync after data refresh
      if (selectedNPC) {
        const updated = npcsRes.data.find(n => n.id === selectedNPC.id);
        if (updated) setSelectedNPC(updated);
        else setSelectedNPC(null);
      }
    } catch { toast.error("Failed to load NPCs"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        campaign_id: campaignId,
        folder_id: formData.folder_id === "none" || !formData.folder_id ? null : formData.folder_id,
      };
      let npcId;
      if (editing) {
        await axios.put(`${API}/npcs/${editing.id}`, payload, { withCredentials: true });
        npcId = editing.id;
        toast.success("NPC updated");
      } else {
        const res = await axios.post(`${API}/npcs`, payload, { withCredentials: true });
        npcId = res.data.id;
        toast.success("NPC created");
      }
      if (imageFile && npcId) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await axios.post(`${API}/npcs/${npcId}/upload`, fd, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
        });
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save NPC"); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete NPC", "Are you sure you want to delete this NPC?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/npcs/${id}`, { withCredentials: true });
      toast.success("NPC deleted");
      if (selectedNPC?.id === id) setSelectedNPC(null);
      fetchData();
    } catch { toast.error("Failed to delete NPC"); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingImageFor) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/npcs/${uploadingImageFor}/upload`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Image uploaded");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploadingImageFor(null); }
  };

  const resetForm = () => {
    setEditing(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData({ name: "", description: "", race: "", profession: "", location_id: "", personality: "", armor_class: "", hit_points: "", speed: "", initiative: "", folder_id: folderCrud.currentFolderId || "" });
  };

  const openEdit = (npc) => {
    setEditing(npc);
    setImageFile(null);
    setImagePreview(npc.image_url && npc.image_url !== "generating" ? resolveImg(npc.image_url) : null);
    setFormData({
      name: npc.name, description: npc.description || "",
      race: npc.race || "", profession: npc.profession || "",
      location_id: npc.location_id || "", personality: npc.personality || "",
      armor_class: npc.armor_class || "", hit_points: npc.hit_points || "",
      speed: npc.speed || "", initiative: npc.initiative || "",
      folder_id: npc.folder_id || ""
    });
    setSelectedNPC(null);
    setDialogOpen(true);
  };

  const handleFormImageSelect = (file) => {
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const getLocationName = (locId) => locations.find(l => l.id === locId)?.name || "";

  const filtered = npcs.filter(n => {
    const matchSearch = n.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchProf = filterProfession === "all" || n.profession === filterProfession;
    return matchSearch && matchProf;
  });
  // Partition into folders + items at current folder level
  const { folders: levelFolders, items: filteredByFolderNpcs } = partitionCurrentLevel({
    folders, items: filtered, currentFolderId: folderCrud.currentFolderId
  });
  // When searching/filtering, show matches across ALL folders (flat), not just the open one.
  const searchingNpcs = searchTerm.trim() !== "" || filterProfession !== "all";
  const displayFolders = searchingNpcs ? [] : levelFolders;
  const displayItems = searchingNpcs ? filtered : filteredByFolderNpcs;

  const moveNpcToFolder = folderCrud.moveItem;

  const generateNpcImage = async (npcId) => {
    const npc = npcs.find(n => n.id === npcId);
    if (!npc) return;
    try {
      toast.info("Generating AI image...");
      await axios.post(`${API}/npcs/${npcId}/generate-image`, { prompt: `D&D fantasy character portrait of ${npc.name}, ${npc.race || ""} ${npc.profession || ""}, ${(npc.description || npc.personality || "").substring(0, 200)}, dark fantasy style, detailed portrait, atmospheric lighting, no text` }, { withCredentials: true });
      toast.success("Image generating — will appear shortly");
      setTimeout(fetchNPCs, 5000);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to generate image"); }
  };

  const bulkMoveNpcs = async (folderId) => {
    const ids = [...multi.selectedIds];
    try {
      await Promise.all(ids.map(id => axios.put(`${API}/campaigns/${campaignId}/npcs/${id}/folder`, { folder_id: folderId }, { withCredentials: true })));
      setNPCs(prev => prev.map(n => ids.includes(n.id) ? { ...n, folder_id: folderId } : n));
      multi.clearAll();
      toast.success(`Moved ${ids.length} NPCs`);
    } catch { toast.error("Failed to move"); }
  };

  const bulkDeleteNpcs = async () => {
    const ids = [...multi.selectedIds];
    if (!window.confirm(`Delete ${ids.length} NPCs?`)) return;
    try {
      await Promise.all(ids.map(id => axios.delete(`${API}/npcs/${id}`, { withCredentials: true })));
      setNPCs(prev => prev.filter(n => !ids.includes(n.id)));
      multi.clearAll();
      toast.success(`Deleted ${ids.length} NPCs`);
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <Layout campaignName="Loading..."><div className="flex justify-center py-20"><div className="spinner" /></div></Layout>;

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="npcs-page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">NPCs</h1>
            <p className="text-muted-foreground text-sm">Non-player characters your adventurers may encounter</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant={multi.selectMode ? "default" : "outline"} className="rounded-sm text-xs" onClick={() => multi.setSelectMode(!multi.selectMode)} data-testid="toggle-select-mode">
              <CheckSquare className="w-3.5 h-3.5 mr-1" /> {multi.selectMode ? "Cancel" : "Select"}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setCreateMode("manual"); setDialogOpen(true); }} data-testid="create-npc-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                <Plus className="w-4 h-4 mr-2" /> New NPC
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{editing ? "Edit NPC" : "New NPC"}</DialogTitle>
              </DialogHeader>
              {!editing && (
                <div className="flex gap-2 border-b border-border pb-3 mb-1">
                  <Button variant={createMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("manual")} data-testid="mode-manual">
                    <Plus className="w-3 h-3 mr-1" /> Create Manually
                  </Button>
                  {!isCommoner && (
                    <Button variant={createMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("ai")} data-testid="mode-ai">
                      <Wand2 className="w-3 h-3 mr-1" /> Use AI
                    </Button>
                  )}
                  {isCommoner && (
                    <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled data-testid="mode-ai-locked">
                      <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                    </Button>
                  )}
                </div>
              )}
              {!editing && createMode === "ai" && (
                <AICreateMode entityType="npc" campaignId={campaignId} onGenerated={(data) => {
                  setFormData(prev => ({ ...prev, ...data }));
                  setCreateMode("manual");
                }} />
              )}
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" style={{ display: (!editing && createMode === "ai") ? "none" : undefined }}>
                <div>
                  <label className="text-sm font-medium block mb-2">NPC Image</label>
                  <ImageDropZone
                    compact
                    onFileSelect={handleFormImageSelect}
                    preview={imagePreview}
                    fileName={imageFile?.name}
                    testId="npc-image-picker"
                    onClear={() => { setImageFile(null); setImagePreview(null); }}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Name *</label>
                  <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Grimjaw the Innkeeper" required data-testid="npc-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium block mb-1">Race</label>
                    <Select value={formData.race} onValueChange={v => setFormData({...formData, race: v})}>
                      <SelectTrigger data-testid="npc-race-select"><SelectValue placeholder="Select race" /></SelectTrigger>
                      <SelectContent>{RACES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Profession</label>
                    <Select value={formData.profession} onValueChange={v => setFormData({...formData, profession: v})}>
                      <SelectTrigger data-testid="npc-profession-select"><SelectValue placeholder="Select profession" /></SelectTrigger>
                      <SelectContent>{PROFESSIONS.filter(Boolean).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium block mb-1">Location</label>
                    <Select value={formData.location_id} onValueChange={v => setFormData({...formData, location_id: v})}>
                      <SelectTrigger data-testid="npc-location-select"><SelectValue placeholder="Where can they be found?" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific location</SelectItem>
                        {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Folder</label>
                    <Select value={formData.folder_id || "none"} onValueChange={v => setFormData({...formData, folder_id: v === "none" ? "" : v})}>
                      <SelectTrigger data-testid="npc-folder-select"><SelectValue placeholder="Unfiled" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unfiled</SelectItem>
                        {folders.filter(f => !f.archived).sort((a, b) => a.name.localeCompare(b.name)).map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Personality</label>
                  <Input value={formData.personality} onChange={e => setFormData({...formData, personality: e.target.value})} placeholder="Gruff but warm-hearted, loves a good tale" data-testid="npc-personality-input" />
                </div>
                {/* Stats Row */}
                <div>
                  <label className="text-sm font-medium block mb-2">Combat Stats</label>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-center">
                      <label className="text-xs text-muted-foreground flex items-center justify-center gap-1 mb-1"><Shield className="w-3 h-3" /> AC</label>
                      <Input className="text-center text-sm h-9" value={formData.armor_class} onChange={e => setFormData({...formData, armor_class: e.target.value})} placeholder="14" data-testid="npc-stat-ac" />
                    </div>
                    <div className="text-center">
                      <label className="text-xs text-muted-foreground flex items-center justify-center gap-1 mb-1"><Heart className="w-3 h-3" /> HP</label>
                      <Input className="text-center text-sm h-9" value={formData.hit_points} onChange={e => setFormData({...formData, hit_points: e.target.value})} placeholder="28" data-testid="npc-stat-hp" />
                    </div>
                    <div className="text-center">
                      <label className="text-xs text-muted-foreground flex items-center justify-center gap-1 mb-1"><Footprints className="w-3 h-3" /> Speed</label>
                      <Input className="text-center text-sm h-9" value={formData.speed} onChange={e => setFormData({...formData, speed: e.target.value})} placeholder="30" data-testid="npc-stat-speed" />
                    </div>
                    <div className="text-center">
                      <label className="text-xs text-muted-foreground flex items-center justify-center gap-1 mb-1"><Eye className="w-3 h-3" /> Init</label>
                      <Input className="text-center text-sm h-9" value={formData.initiative} onChange={e => setFormData({...formData, initiative: e.target.value})} placeholder="+1" data-testid="npc-stat-init" />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Description</label>
                  <RichTextEditor value={formData.description} onChange={v => setFormData({...formData, description: v})} campaignId={campaignId} />
                </div>
                <Button type="submit" className="w-full" data-testid="save-npc-button">{editing ? "Save Changes" : "Create NPC"}</Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search NPCs..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="npc-search" />
          </div>
          <Select value={filterProfession} onValueChange={setFilterProfession}>
            <SelectTrigger className="w-48" data-testid="npc-filter-profession"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Professions</SelectItem>
              {PROFESSIONS.filter(Boolean).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Folder browser */}
        <div className="flex gap-4 items-start">
          <FolderRail
            folders={folders}
            items={npcs}
            currentFolderId={folderCrud.currentFolderId}
            onNavigate={folderCrud.setCurrentFolderId}
            allLabel="All NPCs"
            className="w-44 shrink-0 sticky top-2"
            testIdPrefix="npcs"
          />
          <div className="flex-1 min-w-0">
            <FolderToolbar
              folders={folders}
              currentFolderId={folderCrud.currentFolderId}
              onNavigate={folderCrud.setCurrentFolderId}
              onNewFolder={folderCrud.createHere}
              allLabel="All NPCs"
              className="mb-3"
            />
            {displayFolders.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 mb-4" data-testid="npcs-folder-grid">
                {displayFolders.map(f => (
                  <FolderCard
                    key={f.id}
                    folder={f}
                    count={npcs.filter(n => n.folder_id === f.id).length + folders.filter(x => x.parent_id === f.id && !x.archived).length}
                    onOpen={() => folderCrud.setCurrentFolderId(f.id)}
                    onRename={() => folderCrud.renameFolder(f)}
                    onDelete={() => folderCrud.deleteFolder(f)}
                    onArchive={() => folderCrud.toggleArchive(f)}
                    onColor={(c) => folderCrud.setFolderColor(f, c)}
                    onDropItem={folderCrud.moveItem}
                  />
                ))}
              </div>
            )}

        {displayItems.length === 0 && displayFolders.length === 0 ? (
          <div className="empty-state">
            <UserCircle2 className="empty-state-icon text-muted-foreground w-16 h-16 mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-serif text-foreground mb-2">{npcs.length === 0 ? "No NPCs yet" : "No matches"}</h3>
            <p className="text-muted-foreground mb-6">{npcs.length === 0 ? "Add the characters your party will meet" : "Try a different search"}</p>
            {npcs.length === 0 && <Button onClick={() => { resetForm(); setDialogOpen(true); }}><Plus className="w-4 h-4 mr-2" /> Create NPC</Button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            <input type="file" ref={imageInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
            {displayItems.map(npc => (
              <EntityContextMenu
                key={npc.id}
                onOpen={() => setSelectedNPC(npc)}
                onRename={() => openEdit(npc)}
                onDelete={() => handleDelete(npc.id)}
                folders={folders}
                currentFolderId={npc.folder_id}
                onMoveFolder={(fid) => moveNpcToFolder(npc.id, fid)}
                pinPath="npcs"
                pinLabel={`NPC: ${npc.name}`}
                entityName={npc.name}
              >
                <Card
                  className={`bg-card border-border rounded-sm entity-card overflow-hidden cursor-pointer transition-colors hover:border-primary/40 relative ${multi.isSelected(npc.id) ? "ring-2 ring-primary" : ""}`}
                  onClick={() => multi.selectMode ? multi.toggle(npc.id) : setSelectedNPC(npc)}
                  draggable
                  onDragStart={e => e.dataTransfer.setData("entityId", npc.id)}
                  data-testid={`npc-card-${npc.id}`}
                >
                <FolderColorStrip entityId={npc.id} folderId={npc.folder_id} folders={folders} onFilter={folderCrud.setCurrentFolderId} testIdPrefix="npc" />
                {multi.selectMode && <SelectCheckbox checked={multi.isSelected(npc.id)} onChange={() => multi.toggle(npc.id)} />}
                <CardContent className="p-0">
                  {/* Image */}
                  <div className="h-32 bg-muted/20 relative group">
                    {npc.image_url && npc.image_url !== "generating" ? (
                      <img src={resolveImg(npc.image_url)} alt={npc.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><UserCircle2 className="w-12 h-12 text-muted-foreground/30" /></div>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setUploadingImageFor(npc.id); imageInputRef.current?.click(); }}
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      <Upload className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-serif font-bold text-lg">{npc.name}</h3>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          {npc.race && <span>{npc.race}</span>}
                          {npc.race && npc.profession && <span>·</span>}
                          {npc.profession && <span className="text-primary/80">{npc.profession}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <VisibilityToggle entityType="npcs" entityId={npc.id} currentVisibility={npc.visibility} onUpdate={fetchData} />
                      </div>
                    </div>
                    {npc.personality && <p className="text-xs italic text-muted-foreground mb-2">"{npc.personality}"</p>}
                    {(npc.armor_class || npc.hit_points) && (
                      <div className="flex gap-3 text-xs text-muted-foreground mb-2">
                        {npc.armor_class && <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> AC {npc.armor_class}</span>}
                        {npc.hit_points && <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> HP {npc.hit_points}</span>}
                      </div>
                    )}
                    {npc.location_id && getLocationName(npc.location_id) && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                        <MapPin className="w-3 h-3" /> {getLocationName(npc.location_id)}
                      </div>
                    )}
                    {npc.description && <div className="text-sm text-muted-foreground line-clamp-2"><RichTextDisplay content={npc.description} /></div>}
                  </div>
                </CardContent>
              </Card>
              </EntityContextMenu>
            ))}
          </div>
        )}
          </div>
        </div>

        {/* NPC Detail SlidePanel */}
        <SlidePanel open={!!selectedNPC} onClose={() => setSelectedNPC(null)} title={selectedNPC?.name || "NPC"}
          items={filtered} currentIndex={selectedNPC ? filtered.findIndex(n => n.id === selectedNPC.id) : -1}
          onNavigate={(i) => setSelectedNPC(filtered[i])}
        >
          {selectedNPC && (
            <div data-testid="npc-detail-panel">
              {selectedNPC.image_url && selectedNPC.image_url !== "generating" && (
                <div className="h-48 w-full relative rounded-sm overflow-hidden mb-4">
                  <img src={resolveImg(selectedNPC.image_url)} alt={selectedNPC.name} className="w-full h-full object-cover" />
                </div>
              )}
              {selectedNPC.image_url === "generating" && (
                <div className="h-24 w-full flex items-center justify-center bg-muted/20 rounded-sm mb-4">
                  <p className="text-sm text-muted-foreground animate-pulse">Generating image...</p>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mb-4">
                {selectedNPC.race && <span className="bg-muted/20 border border-border px-2 py-0.5 rounded-sm text-xs">{selectedNPC.race}</span>}
                {selectedNPC.profession && <span className="bg-primary/10 border border-primary/20 text-primary px-2 py-0.5 rounded-sm text-xs">{selectedNPC.profession}</span>}
              </div>

              {/* Personality */}
              {selectedNPC.personality && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Personality</h4>
                  <p className="text-sm italic text-foreground/80" data-testid="npc-detail-personality">"{selectedNPC.personality}"</p>
                </div>
              )}

              {/* Stats */}
              {(selectedNPC.armor_class || selectedNPC.hit_points || selectedNPC.speed || selectedNPC.initiative) && (
                <div className="grid grid-cols-4 gap-2 mb-4" data-testid="npc-detail-stats">
                  {[
                    { label: "AC", value: selectedNPC.armor_class, icon: Shield },
                    { label: "HP", value: selectedNPC.hit_points, icon: Heart },
                    { label: "Speed", value: selectedNPC.speed ? `${selectedNPC.speed}ft` : "", icon: Footprints },
                    { label: "Init", value: selectedNPC.initiative, icon: Eye },
                  ].filter(s => s.value).map(stat => (
                    <div key={stat.label} className="text-center p-2 rounded-sm border border-border bg-muted/10">
                      <stat.icon className="w-3.5 h-3.5 text-primary mx-auto mb-0.5" />
                      <div className="text-lg font-bold text-foreground">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Location */}
              {selectedNPC.location_id && getLocationName(selectedNPC.location_id) && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Location</h4>
                  <div className="flex items-center gap-1.5 text-sm text-foreground/80" data-testid="npc-detail-location">
                    <MapPin className="w-3.5 h-3.5 text-primary" /> {getLocationName(selectedNPC.location_id)}
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedNPC.description && (
                <div className="mb-6">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Description</h4>
                  <div className="text-sm text-foreground/80 prose-sm" data-testid="npc-detail-description">
                    <RichTextDisplay content={selectedNPC.description} />
                  </div>
                </div>
              )}

              {/* Session history feed (Saga20-inspired) */}
              <CharacterSessionHistory characterId={selectedNPC.id} />

              {/* Related links (Phase 6) */}
              <div className="mb-6 pt-4 border-t border-border">
                <RelatedEntitiesPanel entityType="npcs" entityId={selectedNPC.id} />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                <Button variant="outline" size="sm" className="flex-1 rounded-sm" onClick={() => openEdit(selectedNPC)} data-testid="npc-detail-edit">
                  <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => generateNpcImage(selectedNPC.id)} data-testid="npc-generate-image">
                  <Wand2 className="w-3.5 h-3.5 mr-1.5" /> AI Image
                </Button>
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => { setUploadingImageFor(selectedNPC.id); imageInputRef.current?.click(); }} data-testid="npc-detail-upload">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive rounded-sm" onClick={() => handleDelete(selectedNPC.id)} data-testid="npc-detail-delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </SlidePanel>
      </div>
      <BulkActionBar count={multi.count} folders={folders} onBulkMove={bulkMoveNpcs} onBulkDelete={bulkDeleteNpcs} onClear={multi.clearAll} />
    </Layout>
  );
};

export default NPCsPage;
